import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { nationalIdText } from '@/lib/national-id';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { Button, Input, Spinner, SuggestionPopover } from '@/components/ui/primitives';

export interface PatientSummary {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  ageLabel: string;
  phone: string | null;
  category: string;
  dob?: string | null;
  approximateAgeYears?: number | null;
}

/**
 * Type-ahead patient lookup used everywhere a patient must be chosen.
 *
 * Search is debounced and matches MRN, name, phone or CNIC in one field,
 * because that is how a receptionist is given the information — they are told
 * "Zubaida" or handed a card, not asked which field to search.
 */
export function PatientPicker({
  value,
  onChange,
  autoFocus,
  excludeId,
}: {
  value: string;
  onChange: (patientId: string, patient?: PatientSummary) => void;
  autoFocus?: boolean;
  excludeId?: string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PatientSummary | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['patient-search', debounced],
    queryFn: () => get<{ data: PatientSummary[] }>(`/patients${qs({ q: debounced, limit: 8 })}`),
    enabled: debounced.length >= 2,
  });

  // Restores the label when the picker is reopened with a value already set.
  const existing = useQuery({
    queryKey: ['patient', value],
    queryFn: () => get<PatientSummary>(`/patients/${value}`),
    enabled: Boolean(value) && !selected,
  });

  const chosen = selected ?? existing.data ?? null;
  const matches = (results.data?.data ?? []).filter((patient) => patient.id !== excludeId);

  if (value && chosen) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{chosen.fullName}</p>
          <p className="truncate text-xs text-muted-foreground">
            {chosen.mrn} · {chosen.ageLabel} · {chosen.sex}
            {chosen.phone ? ` · ${chosen.phone}` : ''}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={tr('opd', 'clear_patient')}
          onClick={() => {
            setSelected(null);
            onChange('');
            setTerm('');
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <SuggestionPopover
      open={open && debounced.length >= 2}
      onDismiss={() => setOpen(false)}
      anchor={
        <>
          <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="ps-8"
            placeholder={tr('opd', 'mrn_name_phone_or_x', { label: nationalIdText().label })}
            value={term}
            autoFocus={autoFocus}
            onChange={(e) => {
              setTerm(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
        </>
      }
    >
      {results.isFetching && (
        <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
          <Spinner /> {tr('opd', 'searching')}
        </div>
      )}
      {results.data && matches.length === 0 && (
        <p className="p-3 text-sm text-muted-foreground">{tr('opd', 'no_patient_matches_that')}</p>
      )}
      <ul className="max-h-64 overflow-y-auto">
        {matches.map((patient) => (
          <li key={patient.id}>
            <button
              type="button"
              className="w-full px-3 py-2 text-start transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              onClick={() => {
                setSelected(patient);
                onChange(patient.id, patient);
                setOpen(false);
              }}
            >
              <p className="text-sm font-medium">{patient.fullName}</p>
              <p className="text-xs text-muted-foreground">
                {patient.mrn} · {patient.ageLabel} · {patient.sex}
                {patient.phone ? ` · ${patient.phone}` : ''}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </SuggestionPopover>
  );
}
